// Routes API calls to either the remote PHP API or the local offline engine.
// The app layer doesn't know or care which one handles the request.

import DB from './db'

// Endpoints that mutate data - guarded in offline mode.
var WRITE_ENDPOINTS = [
	// Account
	'account/register',
	'account/login',
	'account/logout',
	'account/verify',
	'account/save',
	'account/delete',
	'account/password',
	'account/deep',
	// Sync
	'sync/user',
	'sync/games',
	'sync/process',
	'sync/update',
	// Custom overrides
	'custom/rename',
	'custom/nesting',
	// Tags
	'tags/save',
	'tags/delete',
	'tags/attach',
	'tags/detach',
	'tags/family/save',
	'tags/family/delete',
	// Lists
	'lists/save',
	'lists/delete',
	'lists/edition',
	'lists/reorder',
	'lists/comment',
	// Views
	'views/save',
	'views/delete',
	'views/default',
	// Groups
	'groups/save',
	'groups/delete',
	'groups/join',
	'groups/leave',
	'groups/add',
	'groups/remove',
	'groups/slug',
	// Config
	'config/save',
	// Presets
	'presets/save',
	'presets/delete',
	// Share
	'share/save',
	// Notifications
	'notifications/read',
	// Export
	'export/save',
	// Actions
	'actions/queue',
	// Buddies
	'buddies/save',
	'buddies/delete'
]

// Endpoints that have local handlers implemented.
// Start empty - populated as we build out the offline engine.
var LOCAL_HANDLERS = {}

function resolveHandler(endpoint) {
	if (LOCAL_HANDLERS[endpoint]) {
		return {
			handler: LOCAL_HANDLERS[endpoint],
			params: {}
		}
	}

	var keys = Object.keys(LOCAL_HANDLERS)
	for (var i = 0; i < keys.length; i++) {
		var key = keys[i]
		if (key.indexOf(':') === -1) continue

		var endpointParts = endpoint.split('/')
		var routeParts = key.split('/')
		if (endpointParts.length !== routeParts.length) continue

		var params = {}
		var matched = true

		for (var p = 0; p < routeParts.length; p++) {
			if (routeParts[p].charAt(0) === ':') {
				params[routeParts[p].slice(1)] = endpointParts[p]
				continue
			}

			if (routeParts[p] !== endpointParts[p]) {
				matched = false
				break
			}
		}

		if (matched) {
			return {
				handler: LOCAL_HANDLERS[key],
				params: params
			}
		}
	}

	return null
}

var Router = {

	// Current mode: 'online', 'offline', or 'local' (prefer local, fallback to remote)
	mode: 'online',

	// Is the browser actually connected to the internet?
	connected: (typeof navigator !== 'undefined') ? navigator.onLine : true,

	// Initialize - listen for online/offline events.
	init: function() {
		if (typeof window === 'undefined') return

		window.addEventListener('online', function() {
			Router.connected = true
		})

		window.addEventListener('offline', function() {
			Router.connected = false
		})
	},

	// Set the routing mode.
	// 'online'  - always use remote API (current behaviour)
	// 'offline' - always use local engine (no network)
	// 'local'   - prefer local engine, fall back to remote for unimplemented endpoints
	setMode: function(mode) {
		if (mode === 'online' || mode === 'offline' || mode === 'local') {
			Router.mode = mode
		}
	},

	// Register a local handler for an endpoint.
	// handler signature: function(params) -> Promise<response>
	register: function(endpoint, handler) {
		LOCAL_HANDLERS[endpoint] = handler
	},

	// Should this endpoint be handled locally?
	shouldHandleLocally: function(endpoint) {
		// Online mode: never handle locally
		if (Router.mode === 'online') return false

		// Check if we have a local handler
		if (!resolveHandler(endpoint)) {
			// In offline mode with no handler, we'll error.
			// In local mode, fall back to remote.
			return Router.mode === 'offline'
		}

		// Offline mode or local mode: use local handler if available
		return true
	},

	// Is this a write endpoint?
	isWriteEndpoint: function(endpoint) {
		for (var i = 0; i < WRITE_ENDPOINTS.length; i++) {
			if (endpoint === WRITE_ENDPOINTS[i] || endpoint.indexOf(WRITE_ENDPOINTS[i] + '/') === 0) {
				return true
			}
		}
		return false
	},

	// Handle a request locally.
	// Returns a Promise that resolves with the same response shape as the PHP API.
	handle: function(endpoint, params) {
		var resolved = resolveHandler(endpoint)
		var handler = resolved ? resolved.handler : null

		// Guard write endpoints only when no local implementation exists.
		if (Router.isWriteEndpoint(endpoint) && !handler) {
			return Promise.reject({
				error: 'This action is not available offline.',
				offline: true
			})
		}

		if (!handler) {
			return Promise.reject({
				error: 'Endpoint not available offline: ' + endpoint,
				offline: true,
				unimplemented: true
			})
		}

		// Ensure DB is ready, then run the handler
		return DB.init().then(function() {
			if (!DB.hasData()) {
				return Promise.reject({
					error: 'No offline data available. Please sync while online first.',
					offline: true,
					nodata: true
				})
			}
			return handler(Object.assign({}, params || {}, {
				__offlineRoute: resolved ? resolved.params : {}
			}))
		})
	},

	// Check if the local database has data for a given context.
	hasDataFor: function(type, id) {
		if (!DB.get()) return false
		try {
			if (type === 'users') {
				return DB.scalar('SELECT COUNT(*) FROM user WHERE user_id = ?', [id]) > 0
			} else if (type === 'groups') {
				return DB.scalar('SELECT COUNT(*) FROM groups WHERE group_id = ?', [id]) > 0
			} else if (type === 'buddies') {
				return DB.scalar('SELECT COUNT(*) FROM buddies WHERE buddies_id = ?', [id]) > 0
			}
		} catch (e) {
			return false
		}
		return false
	},

	// Get sync status info for UI display.
	status: function() {
		var lastSync = DB.get() ? DB.meta('last_sync') : null
		return {
			mode: Router.mode,
			connected: Router.connected,
			hasData: DB.get() ? DB.hasData() : false,
			lastSync: lastSync ? parseInt(lastSync, 10) : null
		}
	}

}

export default Router
